/**
 * The law a surface obeys while the finger drags it away — pure decisions, no
 * DOM access and no side effects, so it is testable with synthetic inputs, the
 * same seam `overlay.layout.ts` beside it already draws.
 *
 * It returns numbers. It never reads an element, never writes a style, and owns
 * no pointer. The caller stays the single pointer owner of its own content:
 * a media viewer's own recognizer is the only thing that can tell a pinch from
 * a pan from a page from a dismiss, so the recognizer belongs to the content
 * and only the LAW is shared. A second recognizer on the overlay surface would
 * be usable only by surfaces whose content owns no gesture — which is exactly
 * the set that does not need this.
 *
 * Nothing here knows what fades, or where it sits: `opacity` is the opacity of
 * whatever the caller nominates — the dragged thing itself, a layer behind it,
 * or nothing. The one place that is decided is the call site.
 *
 * Thresholds are policy constants, not magic numbers.
 */

/** Movement past which the gesture's meaning is settled, and held to pointerup. */
export const AXIS_LOCK_PX = 12

/** A gallery reads sideways as paging unless the vertical run beats it by this. */
export const PAGE_BIAS = 1.2

/** The scale a dragged surface never shrinks past, however far it is dragged.
 *  Not a dial: where both external references of a photo-dismissal scale agree.
 *  A curve that reads too subtle is answered by `SCALE_STEEPNESS`, never here. */
export const SCALE_FLOOR = 0.6

/** How deep `ln` is allowed to cut. */
export const SCALE_DEPTH = 0.28

/** `B` in `ln(1 + B·p)`. 12 lands the log curve on the two endpoints of the one
 *  documented scale law for this gesture (1.0 → 0.6 over ~200px) while keeping
 *  the log's shape — steep at the origin so the picture answers the very first
 *  pixels, flat later so it resists collapsing. */
export const SCALE_STEEPNESS = 12

/** How far the fade has travelled by the commit point. 0.45 lands it on 0.55,
 *  which is where the thing being dismissed is still unmistakably present and
 *  unmistakably on its way out — the shipped viewers that translate-and-fade
 *  floor their picture at about half, and none of them run it to zero, because
 *  zero is what the dismissal itself looks like and a threshold must not wear
 *  the same face as the act.
 *
 *  This ramp used to end at 0.28, the house modal dim, when what it drove was a
 *  scrim behind the picture. That ground is gone; the fade is the picture's own
 *  now, and 0.28 was a statement about a dim, not about a photograph. */
export const FADE_DEPTH = 0.45

/** Reduced motion pins the scale at 1, so the fade is the whole signal and
 *  travels nearly twice as far to carry it alone — still stopping well short of
 *  0, for the same reason the default does. */
export const FADE_DEPTH_REDUCED = 0.7

/** The commit distance as a fraction of the viewport, and its clamps. */
export const COMMIT_FRACTION = 0.12
export const COMMIT_MIN_PX = 72
export const COMMIT_MAX_PX = 120

/** Travel below which a release never dismisses, whatever its speed — a hard
 *  gate on real movement, and the dial if accidental dismissals ever appear. */
export const COMMIT_FLOOR_PX = 24

/** How far a release's speed is allowed to carry it. `v · rate/(1 − rate)` is
 *  199ms at PhotoSwipe's 0.995 and 200ms at react-native-awesome-gallery's —
 *  the only two figures validated on dismissal rather than on scrolling.
 *  Apple's own 0.998 is 499ms and is scroll-derived. */
export const PROJECTION_MS = 200

/** The window a release's speed is measured over. Never the whole gesture: a
 *  slow drag ending in a real flick averages out to slow and is refused. */
export const VELOCITY_WINDOW_MS = 100

/** Fewer samples than this in the window and the speed is unmeasured, not slow.
 *  A mean-of-one is a raw Δ/Δt: at a 16ms frame, ±1px of pointer quantisation
 *  is ±12.5px of projection, and one janky 50ms frame clears the commit
 *  distance on its own. */
export const VELOCITY_MIN_SAMPLES = 3

/** Upward pull compresses against this. Near-1:1 at small pull — it answers the
 *  instant the finger moves — and hard-compressing later, so a surface that uses
 *  it cannot be dragged up off the screen. `dragState` no longer applies it: a
 *  picture is thrown off any edge it likes, and a direction that resisted while
 *  its three siblings did not read as a fault rather than a detent. It stays
 *  exported for a surface that does want a top-stop. Deliberately NOT `logDamp`,
 *  whose
 *  `max(0, …)` clamp eats its negative branch and leaves the first ~6.4px dead:
 *  against a 12px lock that is a picture rigid upward and fluid downward, which
 *  reads as a fault rather than as a detent. `logDamp` is a sheet's top-stop and
 *  stays exactly as it is. */
export const UPWARD_GIVE_PX = 48

/** How long after a drag this rule refused before another may arm. A scroll's
 *  momentum settles AFTER pointerup, so the next press 40ms later opens a fresh
 *  session where the scroller genuinely reads 0 and the dismiss would arm on
 *  what the user is experiencing as one continuous scroll. */
export const REFRACTORY_MS = 100

/** The scale a committed dismissal shrinks to as it goes — small enough to read
 *  as gone into the distance rather than merely slid aside. It shrinks about the
 *  bloom origin while travelling along the throw; only a dismissal with no throw
 *  to follow (or one under reduced motion) collapses in place. */
export const DISMISSED_SCALE = 0.1

/** How far past the screen's furthest corner a flung surface is carried, so it
 *  finishes gone rather than parked against the rim. Roughly a phone's half
 *  width, which is more than any picture needs once it is also at
 *  `DISMISSED_SCALE`. */
export const FLING_CLEARANCE_PX = 96

/** The throw a committed dismissal follows: the pointer's total delta at the
 *  release, and the viewport it has to clear. Only the DIRECTION of the delta is
 *  read — how hard it was thrown is already spent deciding that it commits. */
export interface FlingReading {
	dx: number
	dy: number
	viewportWidth: number
	viewportHeight: number
}

/**
 * What the gesture turned out to mean, decided once at `AXIS_LOCK_PX` and held
 * until the pointer lifts. `undecided` is not a third meaning — it is the state
 * before the first one is knowable, and the law still applies through it so the
 * picture answers the very first pixel of movement.
 */
export type DragIntent = 'undecided' | 'page' | 'dismiss'

/** Everything the law needs, all of it already in the caller's hand. */
export interface DragReading {
	/** Pointer delta since press, in CSS px. */
	dx: number
	dy: number
	/** `window.innerHeight`, sampled ONCE at press. The layout viewport and the
	 *  visual viewport differ by the mobile URL bar — 16% on a 15 Pro — and a bar
	 *  moving mid-gesture would otherwise re-scale the commit distance under the
	 *  finger. */
	viewportHeight: number
	/** The press point, in viewport px. */
	grab: { x: number; y: number }
	/** The bloom origin, in viewport px — the point the surface opened out of and
	 *  collapses back onto. Recovered from the origin percentages against the
	 *  viewport, never from a `getBoundingClientRect()` during the drag, which
	 *  returns the POST-transform box and would walk the origin every frame. */
	origin: { x: number; y: number }
	/** A gallery pages sideways at 1×; a single picture is omnidirectional, so a
	 *  sideways throw dismisses it the way a downward one does. */
	gallery: boolean
	reducedMotion: boolean
}

/** The whole frame, as numbers the caller writes wherever it keeps them. */
export interface DragState {
	intent: DragIntent
	/** The scored travel toward dismissal. A single picture is omnidirectional —
	 *  up, down and sideways all score, so it can be thrown off any edge. Only a
	 *  gallery's sideways run scores nothing, because there it is paging. */
	distance: number
	scale: number
	tx: number
	ty: number
	/** Opacity for whatever the caller fades, which is the second thing a drag
	 *  reports with: the scale says how far the finger has come, this says how
	 *  close letting go is to dismissing. */
	opacity: number
}

/** A resting or ended state, in the same shape a frame arrives in. */
export type DragEnd = Pick<DragState, 'scale' | 'tx' | 'ty' | 'opacity'>

/**
 * What the gesture means, from the first frame that has travelled far enough to
 * say. Below the lock nothing is claimed; above it the answer never changes,
 * because a gesture that began as one thing and became another pulls the
 * surface out from under a user who was doing the first thing.
 */
export function lockIntent(dx: number, dy: number, gallery: boolean): DragIntent {
	if (Math.hypot(dx, dy) < AXIS_LOCK_PX) return 'undecided'
	if (!gallery) return 'dismiss'
	return Math.abs(dy) > PAGE_BIAS * Math.abs(dx) ? 'dismiss' : 'page'
}

/** Travel past which a release dismisses on distance alone. */
export function commitDistance(viewportHeight: number): number {
	return Math.min(COMMIT_MAX_PX, Math.max(COMMIT_MIN_PX, COMMIT_FRACTION * viewportHeight))
}

/**
 * How small the picture is at this distance.
 *
 * `ln(1 + B·p)` is steep at the origin and flat after: the first 12px of a
 * 852px-tall viewport already answer with 4% of the width, so the picture reacts
 * the moment the drag starts, and then resists collapsing. Reduced motion pins
 * it at 1 — the drag still tracks the hand 1:1, because direct manipulation IS
 * the user's own movement and removing it leaves the gesture doing nothing, but
 * the scale is the part Apple names as the thing to reduce.
 */
export function scaleAt(distance: number, viewportHeight: number, reducedMotion: boolean): number {
	if (reducedMotion) return 1
	if (!(viewportHeight > 0)) return 1
	const p = Math.max(0, distance) / viewportHeight
	return Math.max(SCALE_FLOOR, 1 - SCALE_DEPTH * Math.log(1 + SCALE_STEEPNESS * p))
}

/**
 * How present the fading thing still is at this distance, as a plain number
 * from 1 down to `1 − FADE_DEPTH`.
 *
 * It is an OPACITY and never a percentage inside a colour: an unregistered
 * custom property carries the universal syntax and does not interpolate, so a
 * value animated through one jumps at 50% progress instead of fading, while an
 * opacity always interpolates and composites off the main thread.
 *
 * Which element it drives is the call site's to decide: today's one caller
 * fades the picture itself, and nothing else, because the picture is the only
 * thing drawn — and because the thing being dismissed is the honest thing to
 * dim.
 */
export function opacityAt(distance: number, viewportHeight: number, reducedMotion: boolean): number {
	const c = Math.min(1, Math.max(0, distance) / commitDistance(viewportHeight))
	return 1 - (reducedMotion ? FADE_DEPTH_REDUCED : FADE_DEPTH) * c
}

/**
 * Upward pull, compressed. `−R·ln(1 + |δ|/R)` yields 3.84px at 4px of pull and
 * 78.8px at 200px: it answers immediately and refuses to let go.
 */
export function rubberBandUp(delta: number): number {
	if (delta >= 0) return delta
	return -UPWARD_GIVE_PX * Math.log(1 + -delta / UPWARD_GIVE_PX)
}

/** How far the release's own speed carries it past where the finger left it. */
export function projectedDistance(distance: number, velocity: number): number {
	return distance + velocity * PROJECTION_MS
}

/**
 * Whether letting go here dismisses. Two gates, and they guard different risks:
 * the floor is a hard gate on real travel, which is what makes an accidental
 * brush safe; the projection is what lets a deliberate flick short of the
 * distance commit, which is what makes the gesture feel answered.
 */
export function commits(inputs: { distance: number; velocity: number; viewportHeight: number }): boolean {
	if (inputs.distance < COMMIT_FLOOR_PX) return false
	return projectedDistance(inputs.distance, inputs.velocity) > commitDistance(inputs.viewportHeight)
}

/**
 * The whole frame.
 *
 * The transform is one origin for the surface's whole lifetime — the bloom
 * origin it opened out of — so drag, snap-back and dismissal are all pure
 * interpolations of `(T, s)` about one point and none of them can jump:
 *
 *     transform-origin: O
 *     transform:        translate(T) scale(s)
 *     T = D + (1 − s)·(G − O)
 *
 * With `transform-origin: O` the matrix maps `P ↦ O + T + s(P − O)`; substitute
 * `T` and the terms cancel identically for every `s`, `O` and `G`, leaving
 * `G + D`. So the grabbed pixel stays exactly under the finger at every scale,
 * with no origin switching and no residual.
 *
 * Translation always follows the finger, on both axes, undamped in every
 * direction: the picture goes where the hand takes it, to any edge of the
 * viewport, and is dismissible off any of them. Upward used to be the exception
 * — scored at zero and compressed by `rubberBandUp` so it could not leave — on
 * the reasoning that a picture should not be draggable off the top. The product
 * owner overruled it: a throw is a throw whichever way it goes, and a direction
 * that resists while its three siblings do not reads as a fault, not a detent.
 * `rubberBandUp` stays exported for callers that still want that top-stop; this
 * law no longer applies it.
 *
 * Only the SCORE is axis-aware, and only for a gallery, where a sideways run is
 * paging and scores nothing toward dismissal.
 */
export function dragState(reading: DragReading, intent: DragIntent): DragState {
	if (intent === 'page') {
		// Paging is not a dismissal and must not look like one: no scale, no
		// fade, and the caller moves the picture its own way.
		return { intent, distance: 0, scale: 1, tx: 0, ty: 0, opacity: 1 }
	}
	const distance = intent === 'dismiss' && reading.gallery ? Math.abs(reading.dy) : Math.hypot(reading.dx, reading.dy)
	const scale = scaleAt(distance, reading.viewportHeight, reading.reducedMotion)
	const dragX = reading.dx
	const dragY = reading.dy
	return {
		intent,
		distance,
		scale,
		tx: dragX + (1 - scale) * (reading.grab.x - reading.origin.x),
		ty: dragY + (1 - scale) * (reading.grab.y - reading.origin.y),
		opacity: opacityAt(distance, reading.viewportHeight, reading.reducedMotion),
	}
}

/** Where a refused gesture returns to. */
export const AT_REST: DragEnd = { scale: 1, tx: 0, ty: 0, opacity: 1 }

/**
 * Where a committed one goes: home to the point it bloomed out of, which is
 * `translate(0,0)` about that same pinned origin. Reduced motion holds the
 * scale and leaves the leaving to opacity, which is what the surface's own
 * reduced-motion exit already does.
 */
export function dismissed(reducedMotion: boolean, fling?: FlingReading): DragEnd {
	const scale = reducedMotion ? 1 : DISMISSED_SCALE
	// No throw to follow, or motion is the thing being reduced: collapse in place.
	// Reduced motion is why this stays a branch rather than a default — a picture
	// flung the width of the screen is precisely the motion that setting removes.
	if (!fling || reducedMotion) return { scale, tx: 0, ty: 0, opacity: 0 }
	const length = Math.hypot(fling.dx, fling.dy)
	if (!(length > 0)) return { scale, tx: 0, ty: 0, opacity: 0 }
	// Carried along its own throw until it is off the screen, whichever way that
	// was. Half the viewport diagonal clears the furthest corner from the centre;
	// the clearance covers the picture's own half-width past that edge, so it is
	// gone rather than resting against the rim. It used to return to `0, 0` — home
	// to the bloom origin — which read as the picture being pulled back out of the
	// hand that had just thrown it away.
	const reach = Math.hypot(fling.viewportWidth, fling.viewportHeight) / 2 + FLING_CLEARANCE_PX
	return { scale, tx: (fling.dx / length) * reach, ty: (fling.dy / length) * reach, opacity: 0 }
}

/**
 * Whether a dismiss may arm over a scroll region.
 *
 * Inert for a viewer, whose mount is `overflow-hidden` and whose stage already
 * takes the whole gesture — it is here for the next surface to adopt the law,
 * which will have one. The caller reads the DOM: it walks `composedPath()` at
 * pointerdown for the nearest node with `scrollHeight > clientHeight` and a
 * computed `overflow-y` of `auto`/`scroll`, CACHES THAT NODE, and reads
 * `scrollTop` off that same node twice. Caching is not an optimisation:
 * scrollability is content-dependent, so an image finishing load between press
 * and lock flips an element into scrollability and the two reads would be of
 * two different elements.
 *
 * `<= 0`, never `=== 0`: iOS reports a negative `scrollTop` while rubber-band
 * overscrolled past the top, which is exactly the state where a pull-to-dismiss
 * is most obviously intended, and fractional device pixel ratios report
 * fractional values.
 *
 * Both reads matter, and the second is the one usually missing: without the
 * at-press read, flinging a list to its top and continuing to drag hands the
 * gesture to dismissal mid-flight. The invariant is that a gesture which begins
 * as a scroll stays a scroll until the pointer lifts.
 *
 * Why a JS rule rather than `touch-action`: `touch-action` is frozen at
 * pointerdown and cannot be switched at the lock. It never needs to be —
 * `scrollTop <= 0` at both samples is precisely the condition under which
 * native scroll has nowhere to go, so the recognizer never suppresses a scroll
 * that was going to happen. The scroller carries `overscroll-behavior-y:
 * contain`, which keeps the chain one node long.
 */
export function scrollAllowsDismiss(reading: {
	scrollTopAtPress: number
	scrollTopAtLock: number
	intent: DragIntent
	sinceRefusedMs: number
}): boolean {
	if (reading.intent !== 'dismiss') return false
	if (reading.sinceRefusedMs < REFRACTORY_MS) return false
	return reading.scrollTopAtPress <= 0 && reading.scrollTopAtLock <= 0
}

/**
 * How fast the drag is moving right now, over the last `VELOCITY_WINDOW_MS`.
 *
 * Stateful but DOM-free, and deliberately one implementation rather than one
 * per adopter: the two rules below are each a bug that is easy to write and
 * impossible to see.
 *
 * Fed the SCORED distance rather than a raw `y`, so it measures speed along
 * whatever the drag's own direction turned out to be.
 */
export class DragVelocity {
	private samples: { t: number; d: number }[] = []

	/** One pointermove: the event's own timestamp, and the scored distance then. */
	sample(timeMs: number, distance: number): void {
		this.samples.push({ t: timeMs, d: distance })
		this.prune(timeMs)
	}

	/**
	 * px/ms at `nowMs` — the release's own timestamp, NOT the last sample's.
	 *
	 * That is the whole point: `pointermove` fires only on movement, so a finger
	 * held still for 150ms and then released leaves the window EMPTY, and an
	 * empty window is zero speed, not the last speed seen. An implementation that
	 * falls back to the last sample projects a 150ms-stale delta forward another
	 * 200ms and dismisses a finger the user deliberately stopped.
	 *
	 * Measured first-to-last across the window rather than as a mean of per-frame
	 * deltas, which is self-normalising against both pointer quantisation and a
	 * single janky frame.
	 */
	velocityAt(nowMs: number): number {
		this.prune(nowMs)
		if (this.samples.length < VELOCITY_MIN_SAMPLES) return 0
		const first = this.samples[0]
		const last = this.samples[this.samples.length - 1]
		const span = last.t - first.t
		if (span <= 0) return 0
		return (last.d - first.d) / span
	}

	reset(): void {
		this.samples = []
	}

	private prune(nowMs: number): void {
		const cutoff = nowMs - VELOCITY_WINDOW_MS
		while (this.samples.length > 0 && this.samples[0].t < cutoff) this.samples.shift()
	}
}
